import React, { useEffect, useRef, Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Layout';
import Home from './pages/Home';
import AboutUs from './pages/AboutUs';
import EquipmentList from './pages/EquipmentList';
import EquipmentDetail from './pages/EquipmentDetail';
import Services from './pages/Services';
import ServiceDetail from './pages/ServiceDetail';
import OurTeam from './pages/OurTeam';
import Contact from './pages/Contact';
import Privacy from './pages/Privacy';
import NotFound from './pages/NotFound';
import OurWork from './pages/OurWork';
import OurWorkDetail from './pages/OurWorkDetail';
import Blogs from './pages/Blogs';
import BlogDetail from './pages/BlogDetail';
import PrivateRoute from './components/PrivateRoute';
import GlobalNetworkLoaderOverlay from './components/GlobalNetworkLoaderOverlay';
import { setGlobalNetworkLoaderSuppressed } from './utils/globalNetworkLoader';
import './App.css';

// Staff/admin-only pages: code-split out of the public bundle since public
// visitors (and Lighthouse) never need this code on first load.
const Login = lazy(() => import('./pages/Login'));
const ForgotPassword = lazy(() => import('./pages/ForgotPassword'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const Dashboard = lazy(() => import('./pages/Dashboard'));
const Profile = lazy(() => import('./pages/Profile'));
const MyEvents = lazy(() => import('./pages/MyEvents'));
const Courses = lazy(() => import('./pages/Courses'));
const CourseDetail = lazy(() => import('./pages/CourseDetail'));
const EventDetail = lazy(() => import('./pages/EventDetail'));
const Earnings = lazy(() => import('./pages/Earnings'));
const AddEditEvent = lazy(() => import('./pages/AddEditEvent'));
const AllMembersSalaries = lazy(() => import('./pages/AllMembersSalaries'));
const ClientsBalance = lazy(() => import('./pages/ClientsBalance'));
const FinancialBalance = lazy(() => import('./pages/FinancialBalance'));
const AdminFinancesPanel = lazy(() => import('./pages/AdminFinancesPanel'));
const AdminStaffMembers = lazy(() => import('./pages/AdminStaffMembers'));
const ChatsList = lazy(() => import('./pages/ChatsList'));
const ChatRoom = lazy(() => import('./pages/ChatRoom'));

const ROUTE_SUPPRESS_MS = 800;
const GA_MEASUREMENT_ID = 'G-QPLYGM42MG';

function GARouteTracker() {
  const location = useLocation();

  useEffect(() => {
    if (typeof window.gtag !== 'function') return;
    window.gtag('config', GA_MEASUREMENT_ID, {
      page_path: `${location.pathname}${location.search}`,
    });
  }, [location.pathname, location.search]);

  return null;
}

function RouteChangeLoaderSuppression() {
  const location = useLocation();
  const timeoutRef = useRef(null);

  useEffect(() => {
    setGlobalNetworkLoaderSuppressed(true);
    if (timeoutRef.current) {
      window.clearTimeout(timeoutRef.current);
    }
    timeoutRef.current = window.setTimeout(() => {
      setGlobalNetworkLoaderSuppressed(false);
      timeoutRef.current = null;
    }, ROUTE_SUPPRESS_MS);
    return () => {
      if (timeoutRef.current) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [location.pathname, location.search, location.hash]);

  useEffect(() => () => setGlobalNetworkLoaderSuppressed(false), []);
  return null;
}

function App() {
  return (
    <AuthProvider>
      <Router future={{ v7_relativeSplatPath: true }}>
        <RouteChangeLoaderSuppression />
        <GARouteTracker />
        <Layout>
          <Suspense fallback={null}>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<Home />} />
            <Route path="/about" element={<AboutUs />} />
            <Route path="/about/equipment" element={<EquipmentList />} />
            <Route path="/about/equipment/:slug" element={<EquipmentDetail />} />
            <Route path="/login" element={<Login />} />
            <Route path="/forgot-password" element={<ForgotPassword />} />
            <Route path="/reset-password/:uid/:token" element={<ResetPassword />} />
            <Route path="/services" element={<Services />} />
            <Route path="/services/:slug" element={<ServiceDetail />} />
            <Route path="/team" element={<OurTeam />} />
            <Route path="/contact" element={<Contact />} />
            <Route path="/privacy" element={<Privacy />} />
            <Route path="/history/our-work" element={<OurWork />} />
            <Route path="/history/our-work/:slug" element={<OurWorkDetail />} />
            <Route path="/history/blogs" element={<Blogs />} />
            <Route path="/history/blogs/:slug" element={<BlogDetail />} />
            <Route path="/register" element={<Navigate to="/" replace />} />
            
            {/* Authenticated Routes */}
            <Route path="/profile" element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            } />
            <Route path="/profile/:userId" element={
              <PrivateRoute>
                <Profile />
              </PrivateRoute>
            } />
            
            {/* Company Member Routes */}
            <Route path="/dashboard" element={
              <PrivateRoute requiredRole="member">
                <Dashboard />
              </PrivateRoute>
            } />
            <Route path="/my-events" element={
              <PrivateRoute requiredRole="member">
                <MyEvents />
              </PrivateRoute>
            } />
            <Route path="/courses" element={
              <PrivateRoute requiredRole="member">
                <Courses />
              </PrivateRoute>
            } />
            <Route path="/courses/:slug" element={
              <PrivateRoute requiredRole="member">
                <CourseDetail />
              </PrivateRoute>
            } />
            <Route path="/courses/:slug/lessons/:lessonId" element={
              <PrivateRoute requiredRole="member">
                <CourseDetail />
              </PrivateRoute>
            } />
            <Route path="/events/:slug" element={
              <PrivateRoute requiredRole="member">
                <EventDetail />
              </PrivateRoute>
            } />
            <Route path="/earnings" element={
              <PrivateRoute requiredRole="member">
                <Earnings />
              </PrivateRoute>
            } />
            <Route path="/chats" element={
              <PrivateRoute requiredRole="member">
                <ChatsList />
              </PrivateRoute>
            } />
            <Route path="/chats/:roomId" element={
              <PrivateRoute requiredRole="member">
                <ChatRoom />
              </PrivateRoute>
            } />
            
            {/* Company Admin Routes */}
            <Route path="/admin/events" element={
              <PrivateRoute requiredRole="admin">
                <MyEvents />
              </PrivateRoute>
            } />
            <Route path="/admin/events/add" element={
              <PrivateRoute requiredRole="admin">
                <AddEditEvent />
              </PrivateRoute>
            } />
            <Route path="/admin/events/:slug/edit" element={
              <PrivateRoute requiredRole="admin">
                <AddEditEvent />
              </PrivateRoute>
            } />
            <Route path="/admin/salaries" element={
              <PrivateRoute requiredRole="admin">
                <AllMembersSalaries />
              </PrivateRoute>
            } />
            <Route path="/admin/balance" element={
              <PrivateRoute requiredRole="admin">
                <ClientsBalance />
              </PrivateRoute>
            } />
            <Route path="/admin/financial-balance" element={
              <PrivateRoute requiredRole="admin">
                <FinancialBalance />
              </PrivateRoute>
            } />
            <Route path="/admin/finances-panel" element={
              <PrivateRoute requiredRole="admin">
                <AdminFinancesPanel />
              </PrivateRoute>
            } />
            <Route path="/admin/staff-members" element={
              <PrivateRoute requiredRole="admin">
                <AdminStaffMembers />
              </PrivateRoute>
            } />

            {/* Catch-all: must stay last */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </Suspense>
        </Layout>
        <GlobalNetworkLoaderOverlay />
      </Router>
    </AuthProvider>
  );
}

export default App;

